import React, { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import CategoryManager from './components/CategoryManager';
import ExpenseForm from './components/ExpenseForm';
import ExpenseList from './components/ExpenseList';
import ExpenseAnalytics from './components/ExpenseAnalytics';
import PortfolioSummary from './components/PortfolioSummary';
import AssetAllocation from './components/AssetAllocation';
import AssetList from './components/AssetList';
import HoldingsList from './components/HoldingsList';
import TransactionForm from './components/TransactionForm';
import TransactionHistory from './components/TransactionHistory';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './contexts/AuthContext';

// Main app content component (shown when authenticated)
const AppContent: React.FC = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [transactionRefreshTrigger, setTransactionRefreshTrigger] = useState(0);
  const { logout } = useAuth();

  const handleExpenseAdded = () => {
    // Trigger refresh of expense list
    setRefreshTrigger(prev => prev + 1);
  };

  const handleTransactionAdded = () => {
    // Trigger refresh of transaction history
    setTransactionRefreshTrigger(prev => prev + 1);
  };

  return (
    <Tabs.Root defaultValue="expenses">
      <div className="min-h-screen text-white font-inter">
        <header className="sticky top-0 z-50 border-b border-white/10 bg-black/60 backdrop-blur">
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-md grid place-items-center text-xs font-bold text-white/90 bg-white/5 border border-white/10 shadow-[0_0_0_1px_rgba(34,211,238,0.15)_inset,0_4px_20px_-10px_rgba(99,102,241,0.6)]">
                  PF
                </div>
                <span className="text-[17px] font-semibold tracking-tight">Personal Finance</span>
              </div>
              <div className="flex items-center gap-4">
                <Tabs.List className="flex items-center space-x-2 sm:space-x-6">
                  <Tabs.Trigger
                    value="expenses"
                    className="relative px-3 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors after:content-[''] after:block after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-0.5 after:origin-left after:scale-x-0 after:rounded-full after:transition-transform after:duration-200 after:bg-[linear-gradient(to_right,#c09a6f,#d4b896)] data-[state=active]:text-white data-[state=active]:after:scale-x-100"
                  >
                    Expenses
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value="investments"
                    className="relative px-3 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors after:content-[''] after:block after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-0.5 after:origin-left after:scale-x-0 after:rounded-full after:transition-transform after:duration-200 after:bg-[linear-gradient(to_right,#c09a6f,#d4b896)] data-[state=active]:text-white data-[state=active]:after:scale-x-100"
                  >
                    Investments
                  </Tabs.Trigger>
                </Tabs.List>
                <button
                  onClick={logout}
                  className="px-3 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-md hover:bg-white/5 border border-transparent hover:border-white/10"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-6 py-8">
          <Tabs.Content value="expenses" className="space-y-6">
            {/* Nested Tabs for Expenses Section */}
            <Tabs.Root defaultValue="expenses-list">
              <div className="mb-6">
                <Tabs.List className="flex items-center space-x-1 bg-white/5 rounded-lg border border-white/10 p-1">
                  <Tabs.Trigger
                    value="expenses-list"
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-md data-[state=active]:bg-[var(--accent-gold)] data-[state=active]:text-white"
                  >
                    Expenses
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value="analytics"
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-md data-[state=active]:bg-[var(--accent-gold)] data-[state=active]:text-white"
                  >
                    Analytics
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value="categories"
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-md data-[state=active]:bg-[var(--accent-gold)] data-[state=active]:text-white"
                  >
                    Categories
                  </Tabs.Trigger>
                </Tabs.List>
              </div>

              {/* Expenses Sub-tab */}
              <Tabs.Content value="expenses-list" className="space-y-6">
                <ExpenseList refreshTrigger={refreshTrigger} onExpenseAdded={handleExpenseAdded} />
              </Tabs.Content>

              {/* Analytics Sub-tab */}
              <Tabs.Content value="analytics">
                <ExpenseAnalytics />
              </Tabs.Content>

              {/* Categories Sub-tab */}
              <Tabs.Content value="categories">
                <div className="max-w-4xl mx-auto">
                  <CategoryManager />
                </div>
              </Tabs.Content>
            </Tabs.Root>
          </Tabs.Content>

          <Tabs.Content value="investments" className="space-y-6">
            {/* Nested Tabs for Investments Section */}
            <Tabs.Root defaultValue="portfolio">
              <div className="mb-6">
                <Tabs.List className="flex items-center space-x-1 bg-white/5 rounded-lg border border-white/10 p-1">
                  <Tabs.Trigger
                    value="portfolio"
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-md data-[state=active]:bg-[var(--accent-gold)] data-[state=active]:text-white"
                  >
                    Portfolio
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value="assets"
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-md data-[state=active]:bg-[var(--accent-gold)] data-[state=active]:text-white"
                  >
                    Assets
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value="transactions"
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-300 hover:text-white transition-colors rounded-md data-[state=active]:bg-[var(--accent-gold)] data-[state=active]:text-white"
                  >
                    Transactions
                  </Tabs.Trigger>
                </Tabs.List>
              </div>

              {/* Portfolio Sub-tab */}
              <Tabs.Content value="portfolio" className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-3xl font-semibold mb-2 tracking-tight">
                    <span className="brand-gradient text-gradient">Investment Portfolio</span>
                  </h2>
                  <p className="text-slate-400">Track your investments and monitor performance</p>
                </div>

                <PortfolioSummary />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <HoldingsList />
                  </div>
                  <div>
                    <AssetAllocation />
                  </div>
                </div>
              </Tabs.Content>

              {/* Assets Sub-tab */}
              <Tabs.Content value="assets" className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-3xl font-semibold mb-2 tracking-tight">
                    <span className="brand-gradient text-gradient">Manage Assets</span>
                  </h2>
                  <p className="text-slate-400">View and manage your investment assets</p>
                </div>

                <AssetList />
              </Tabs.Content>

              {/* Transactions Sub-tab */}
              <Tabs.Content value="transactions" className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-3xl font-semibold mb-2 tracking-tight">
                    <span className="brand-gradient text-gradient">Transactions</span>
                  </h2>
                  <p className="text-slate-400">Record and view your investment transactions</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-1">
                    <div className="rounded-xl border border-white/10 bg-white/5 p-6 sticky top-6">
                      <h3 className="text-lg font-semibold mb-4">Record Transaction</h3>
                      <TransactionForm
                        key={transactionRefreshTrigger}
                        onSuccess={handleTransactionAdded}
                      />
                    </div>
                  </div>

                  <div className="lg:col-span-2">
                    <TransactionHistory key={transactionRefreshTrigger} />
                  </div>
                </div>
              </Tabs.Content>
            </Tabs.Root>
          </Tabs.Content>
        </main>
      </div>
    </Tabs.Root>
  );
};

// Main App component with authentication
function App() {
  return (
    <ProtectedRoute>
      <AppContent />
    </ProtectedRoute>
  );
}

export default App;